import { create } from 'zustand'
import {
  AssistantRequestError,
  fetchAssistantStatus,
  fetchConversation,
  fetchConversations,
  archiveConversation,
  actOnNudge,
  fetchNudges,
  streamChat,
  streamDecision,
  type AssistantEvent,
  type AssistantUsage,
  type ConversationSummary,
  type Nudge,
  type NudgeAction,
  type TranscriptItem,
} from '@/lib/assistant'

/**
 * The assistant panel's state (components/Assistant). One conversation is on
 * screen at a time; the stream's events are folded into `items` as they land.
 */
interface AssistantState {
  open: boolean
  configured: boolean | null
  usage: AssistantUsage | null
  conversationId: string | null
  items: TranscriptItem[]
  busy: boolean
  history: ConversationSummary[]
  setOpen: (open: boolean) => void
  toggle: () => void
  refreshStatus: () => Promise<void>
  loadHistory: () => Promise<void>
  newConversation: () => void
  openConversation: (id: string) => Promise<void>
  removeConversation: (id: string) => Promise<void>
  /** `shownAs` is what the bubble says when the message is an instruction (a nudge's Do it). */
  send: (text: string, shownAs?: string) => Promise<void>
  decide: (toolUseId: string, decision: 'confirm' | 'cancel') => Promise<void>
  /** Proactive suggestions waiting for this person (empty when Proactive is off). */
  nudges: Nudge[]
  refreshNudges: (force?: boolean) => Promise<void>
  /** "Do it" opens a fresh conversation and hands Ask AI the nudge's instruction. */
  actNudge: (id: string, action: NudgeAction) => Promise<void>
}

let lastNudgeFetch = 0

/** Fold one stream event into the transcript. Pure, so it is unit-tested. */
export function applyEvent(items: TranscriptItem[], event: AssistantEvent): TranscriptItem[] {
  switch (event.type) {
    case 'text': {
      const last = items[items.length - 1]
      if (last?.type === 'assistant') return [...items.slice(0, -1), { ...last, text: last.text + event.delta }]
      return [...items, { type: 'assistant', text: event.delta.replace(/^\s+/, '') }]
    }
    case 'tool_call': {
      // A confirmed call comes back through here: the card becomes a running tool.
      const i = items.findIndex((it) => (it.type === 'tool' || it.type === 'confirm') && it.id === event.id)
      const item: TranscriptItem = { type: 'tool', id: event.id, name: event.name, title: event.title, status: 'running', summary: '' }
      if (i === -1) return [...items, item]
      const next = [...items]
      next[i] = item
      return next
    }
    case 'tool_result': {
      const i = items.findIndex((it) => (it.type === 'tool' || it.type === 'confirm') && it.id === event.id)
      const status = event.ok ? 'ok' : event.summary === 'Cancelled' ? 'cancelled' : 'error'
      const prev = items[i]
      const item: TranscriptItem = {
        type: 'tool',
        id: event.id,
        name: event.name,
        title: prev && 'title' in prev ? prev.title : undefined,
        status,
        summary: event.summary,
      }
      if (i === -1) return [...items, item]
      const next = [...items]
      next[i] = item
      return next
    }
    case 'confirm':
      return [...items, { type: 'confirm', id: event.id, name: event.name, title: event.title, kind: event.kind, input: event.input, target: event.target }]
    case 'error':
      return [...items, { type: 'error', text: event.message }]
    default:
      return items
  }
}

export const useAssistantStore = create<AssistantState>((set, get) => {
  const onEvent = (event: AssistantEvent) => {
    if (event.type === 'conversation') {
      if (get().conversationId !== event.id) set({ conversationId: event.id })
      return
    }
    if (event.type === 'usage') {
      const { type: _t, ...usage } = event
      set({ usage })
      return
    }
    set({ items: applyEvent(get().items, event) })
  }

  const refuse = (err: unknown) => {
    if (err instanceof AssistantRequestError) {
      if (err.usage) set({ usage: err.usage })
      if (err.code === 'NOT_CONFIGURED') set({ configured: false })
      if (err.code === 'assistant_paused') return
      set({ items: [...get().items, { type: 'error', text: err.message }] })
      return
    }
    set({ items: [...get().items, { type: 'error', text: 'Lost the connection. Check your network and try again.' }] })
  }

  return {
    open: false,
    configured: null,
    usage: null,
    conversationId: null,
    items: [],
    busy: false,
    history: [],
    nudges: [],
    setOpen: (open) => {
      set({ open })
      if (open && get().configured === null) void get().refreshStatus()
      if (open) void get().refreshNudges(true)
    },
    refreshNudges: async (force = false) => {
      // Two buttons (sidebar + header) and the panel all ask; one fetch serves them.
      if (!force && Date.now() - lastNudgeFetch < 20_000) return
      lastNudgeFetch = Date.now()
      try {
        set({ nudges: await fetchNudges() })
      } catch {
        /* suggestions are extra; the panel works without them */
      }
    },
    actNudge: async (id, action) => {
      const before = get().nudges
      const nudge = before.find((n) => n.id === id)
      set({ nudges: before.filter((n) => n.id !== id) })
      try {
        const res = await actOnNudge(id, action)
        if (action === 'do' && res.ask && !get().busy) {
          set({ open: true, conversationId: null, items: [] })
          await get().send(res.ask, nudge ? `${nudge.actionLabel ?? 'Do it'}: ${nudge.text}` : undefined)
        }
      } catch {
        set({ nudges: before })
      }
    },
    toggle: () => get().setOpen(!get().open),
    refreshStatus: async () => {
      try {
        const s = await fetchAssistantStatus()
        set({ configured: s.configured, usage: s.usage })
      } catch {
        /* the panel still works; the next send reports any problem */
      }
    },
    loadHistory: async () => {
      try {
        set({ history: await fetchConversations() })
      } catch {
        set({ history: [] })
      }
    },
    newConversation: () => {
      if (get().busy) return
      set({ conversationId: null, items: [] })
    },
    openConversation: async (id) => {
      if (get().busy) return
      const c = await fetchConversation(id)
      set({ conversationId: c.id, items: c.items })
    },
    removeConversation: async (id) => {
      await archiveConversation(id)
      set({ history: get().history.filter((h) => h.id !== id) })
      if (get().conversationId === id) set({ conversationId: null, items: [] })
    },
    send: async (text, shownAs) => {
      const message = text.trim()
      if (!message || get().busy) return
      // Anything still waiting is cancelled by the server when a new message lands.
      const items = get().items.map((it): TranscriptItem =>
        it.type === 'confirm' ? { type: 'tool', id: it.id, name: it.name, title: it.title, status: 'cancelled', summary: 'Cancelled' } : it,
      )
      set({ busy: true, items: [...items, { type: 'user', text: shownAs ?? message }] })
      try {
        await streamChat({ message, conversationId: get().conversationId }, onEvent)
      } catch (err) {
        refuse(err)
      } finally {
        set({ busy: false })
      }
    },
    decide: async (toolUseId, decision) => {
      const id = get().conversationId
      if (!id || get().busy) return
      set({
        busy: true,
        items: get().items.map((it) => (it.type === 'confirm' && it.id === toolUseId ? { ...it, deciding: true } : it)),
      })
      try {
        await streamDecision(id, toolUseId, decision, onEvent)
      } catch (err) {
        set({ items: get().items.map((it) => (it.type === 'confirm' && it.id === toolUseId ? { ...it, deciding: false } : it)) })
        refuse(err)
      } finally {
        set({ busy: false })
      }
    },
  }
})
